import * as React from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import {
    Button, Typography, CircularProgress, TextField, ButtonBase, Switch, FormControlLabel,
} from '@material-ui/core';
import { contactEmail } from '../../../common/constants';

import * as AuthStore from '../../../app/store/AuthStore';
import Auth from '../../../youwell-common/Auth';
import urls from '../../../app/urls';

const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    loginDescription: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    textField: {
        marginBottom: theme.spacing(2),
    },
    textFieldPin: {
        marginRight: theme.spacing(8),
        width: 100,
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(3),
    },
    pinLogin: {
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    subActions: {
    },
    infoContainer: {
        marginTop: theme.spacing(3),
    },
    loading: {
        height: 200,
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    patientLogin: {
        marginTop: theme.spacing(8),
        borderTop: theme.borders.green,
        paddingTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    studentLoginButton: {
        alignSelf: 'center',
    },
});

const getErrorMessage = (translate, errorMessage, is2FALogin) => {
    switch (errorMessage) {
    case '1065':
        if (is2FALogin) {
            return translate('login.twoFactorPinFailed');
        }
        break;
    case '1106':
    case '1107':
        return translate('login.missingPhoneFor2FA', { contactEmail });
    default:
        break;
    }
    return translate(`errors.${errorMessage}`, null, { onMissingTranslation: () => `System error (${errorMessage})` });
};


class Login extends React.Component {
    state = {
        email: '',
        password: '',
        pin: '',
        addToTrustedDevices: false,
        patientCode: '',
    };

    componentDidMount() {
        const redirect = this.props.match.params.redirect ? decodeURIComponent(this.props.match.params.redirect) : urls.inside.home;
        if (this.props.match.params.code) {
            this.props.loginWithCode(this.props.match.params.code, this.props.match.params.provider, this.props.match.params.state, { redirect });
        } else if (Auth.isUserAuthenticated()) {
            this.props.getUserProfile({ redirect });
        }
    }

    handleChange = name => (event) => {
        this.setState({ [name]: event.target.value });
    };

    handleAddToTrustedDevicesChange = (e) => {
        this.setState({
            addToTrustedDevices: e.target.checked,
        });
    };

    handleTabChange = name => (event, value) => {
        this.setState({
            [name]: value,
        });
    }

    handleKeyPressUserPwd = (e) => {
        if (e.key === 'Enter' && this.state.email && this.state.password) {
            this.login();
        }
    };

    handleKeyPressPin = (e) => {
        if (e.key === 'Enter' && this.state.pin) {
            this.login();
        }
    };

    handleKeyPressPatientCode = (e) => {
        if (e.key === 'Enter' && this.state.patientCode) {
            this.loginPatientCode();
        }
    };

    loginPatientCode = () => {
        if (this.state.patientCode) {
            this.props.redirect(`${urls.studentCode.home}/${this.state.patientCode}`);
        }
    }

    login = () => {
        const redirect = this.props.match.params.redirect ? decodeURIComponent(this.props.match.params.redirect) : urls.inside.home;

        if (this.props.twoFactorId) {
            if (!this.state.pin) {
                return;
            }
            this.props.loginWithPin(this.state.pin, this.props.twoFactorId, this.state.addToTrustedDevices, { redirect });
        } else {
            if (!this.state.email || !this.state.password) {
                return;
            }
            const email = this.state.email.indexOf('@') > -1 ? this.state.email : `${this.state.email}@vimo.no`;
            this.props.login(email, this.state.password, { redirect });
        }
    };

    cancelTwoFactorLogin = () => {
        this.props.cancelTwoFactor();
    }

    render() {
        const {
            classes, translate, errorMessage, loading, match, logoutReason, twoFactorId,
        } = this.props;

        const showTwoFactorPinLogin = !!twoFactorId;
        const serverLogin = match.params.code || Auth.isUserAuthenticated();
        const showStudentLogin = match.params.redirect === 'student';

        return (
            <div className={classes.root}>
                <div className={classes.header}>
                    <Typography variant="h6">
                        {translate('login.heading')}
                    </Typography>
                </div>

                {loading ? (
                    <div className={classes.loading}>
                        <CircularProgress />
                        {serverLogin && !errorMessage && (
                            <Typography color="textSecondary" className={classes.loginDescription}>
                                {translate('login.loginInProgress')}
                            </Typography>
                        )}
                    </div>
                ) : (
                    <>
                        {showTwoFactorPinLogin ? (
                            <div>
                                <Typography color="textSecondary" className={classes.loginDescription}>
                                    {translate('login.twoFactorPinDescription')}
                                </Typography>
                                <div className={classes.pinLogin}>
                                    <TextField
                                        id="pin"
                                        type="number"
                                        label={translate('login.pin')}
                                        className={classes.textFieldPin}
                                        value={this.state.pin}
                                        onChange={this.handleChange('pin')}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            onKeyPress: this.handleKeyPressPin,
                                        }}
                                    />
                                    <Button onClick={this.login} variant="contained" color="primary" disabled={loading}>
                                        {translate('login.login')}
                                    </Button>
                                </div>
                                <FormControlLabel
                                    control={(
                                        <Switch
                                            color="secondary"
                                            checked={this.state.addToTrustedDevices}
                                            onChange={this.handleAddToTrustedDevicesChange}
                                        />
                                    )}
                                    label={translate('login.addToTrustedDevices')}
                                />
                            </div>
                        ) : (
                            <>
                                <TextField
                                    id="email"
                                    type="email"
                                    label={translate('login.email')}
                                    className={classes.textField}
                                    value={this.state.email}
                                    onChange={this.handleChange('email')}
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        onKeyPress: this.handleKeyPressUserPwd,
                                    }}
                                />
                                <TextField
                                    id="password"
                                    type="password"
                                    label={translate('login.password')}
                                    className={classes.textField}
                                    value={this.state.password}
                                    onChange={this.handleChange('password')}
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        onKeyPress: this.handleKeyPressUserPwd,
                                    }}
                                />
                            </>
                        )}

                        {showTwoFactorPinLogin ? (
                            <div className={classes.actionButtons}>
                                <ButtonBase onClick={this.cancelTwoFactorLogin} className={classes.forgotPassword}>
                                    <Typography variant="body2" align="right">
                                        {translate('login.cancel')}
                                    </Typography>
                                </ButtonBase>
                            </div>
                        ) : (
                            <>
                                <div className={classes.actionButtons}>
                                    <Link to={urls.register}>
                                        <Typography color="primary">
                                            {translate('login.registerNewUser')}
                                        </Typography>
                                    </Link>

                                    <Button onClick={this.login} variant="contained" color="primary" disabled={loading}>
                                        {translate('login.login')}
                                    </Button>
                                </div>
                                <div className={classes.subActions}>
                                    <Link to={`${urls.login}/${urls.loginParts.forgotPassword}`}>
                                        <Typography color="primary">
                                            {translate('login.forgotPassword')}
                                        </Typography>
                                    </Link>
                                </div>
                            </>
                        )}
                    </>
                )}

                <div className={classes.infoContainer}>
                    {logoutReason && (
                        <Typography color="secondary" gutterBottom>
                            {translate(`errors.${logoutReason}`, null, { onMissingTranslation: () => translate('login.youHaveBeenLoggedOut') })}
                        </Typography>
                    )}
                    {errorMessage && (
                        <Typography color="error" align="center" display="block">
                            {getErrorMessage(translate, errorMessage, showTwoFactorPinLogin)}
                        </Typography>
                    )}
                </div>

                {showStudentLogin && (
                    <div className={classes.patientLogin}>
                        <div className={classes.header}>
                            <Typography variant="h6">
                                {translate('login.studentLogin')}
                            </Typography>
                        </div>
                        <TextField
                            id="patientCode"
                            label={translate('login.studentCode')}
                            className={classes.textField}
                            value={this.state.patientCode}
                            onChange={this.handleChange('patientCode')}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                onKeyPress: this.handleKeyPressPatientCode,
                            }}
                        />
                        <Button className={classes.studentLoginButton} onClick={this.loginPatientCode} variant="contained" color="primary" disabled={loading}>
                            {translate('login.loginWithStudentCode')}
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

Login.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    getUserProfile: PropTypes.func.isRequired,
    login: PropTypes.func.isRequired,
    loginWithPin: PropTypes.func.isRequired,
    loginWithCode: PropTypes.func.isRequired,
    cancelTwoFactor: PropTypes.func.isRequired,
    redirect: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    errorMessage: PropTypes.string,
    twoFactorId: PropTypes.string,
    logoutReason: PropTypes.string,
};

Login.defaultProps = {
    loading: false,
    errorMessage: null,
    logoutReason: null,
    twoFactorId: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    loading: state.auth.loading,
    errorMessage: [].concat(state.auth.errors).filter(val => val).join('<br/>'),
    logoutReason: state.auth.logoutReason,
    twoFactorId: state.auth.twoFactorId,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(AuthStore.actionCreators, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Login)));
